import React, { useEffect, useMemo, useRef, useState } from "react";
import { deleteObject, getMetadata, listAll, ref } from "firebase/storage";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { storage } from "../../../lib/firebase";
import { getAttachmentConfig } from "../../../config/smartTagAttachments";
import type {
  SmartTagAttachmentFile,
  SmartTagAttachmentKey,
  VCardCategory,
} from "../../../types/vcard";
import {
  AttachmentValidationError,
  buildProtectedUploadFileName,
  fetchAttachmentBlob,
  resolveAttachmentScanStatus,
  type AttachmentScanStatus,
  uploadProtectedFile,
  validateAttachmentFile,
} from "../../../services/storage/malwareScan";

type FileStatusMap = Record<string, AttachmentScanStatus>;

export interface ProtectedDocumentUploadPanelProps {
  userId: string;
  vcardCategory: VCardCategory;
  attachmentKey: SmartTagAttachmentKey;
  files: SmartTagAttachmentFile[];
  onFilesChange: (nextFiles: SmartTagAttachmentFile[]) => void | Promise<void>;
  disabled?: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
}

const createAttachmentId = (): string => {
  const browserCrypto = globalThis.crypto;

  if (browserCrypto && typeof browserCrypto.randomUUID === "function") {
    return browserCrypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getStorageDisplayName = (storageName: string): string =>
  storageName.replace(/^\d+-/, "") || storageName;

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 KB";
  }

  const mb = bytes / (1024 * 1024);
  if (mb >= 1) {
    return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof AttachmentValidationError) {
    if (error.code === "unsupported_file_type") {
      return "This file type is not supported for this attachment.";
    }

    if (error.code === "file_too_large") {
      return "This file is too large for this attachment.";
    }

    if (error.code === "scan_rejected") {
      return "The malware scan rejected this document. Please choose a different file.";
    }

    if (error.code === "scan_timed_out") {
      return "The document is still being scanned. Please try again shortly.";
    }
  }

  return error instanceof Error
    ? error.message
    : "Upload failed. Please try again.";
};

const getStatusTone = (
  status: AttachmentScanStatus | undefined,
): {
  label: string;
  className: string;
  Icon: typeof Clock;
} => {
  if (status === "clean") {
    return {
      label: "Clean",
      className: "bg-green-50 text-green-700",
      Icon: ShieldCheck,
    };
  }

  if (status === "infected" || status === "error" || status === "missing") {
    return {
      label: status === "missing" ? "Missing" : "Failed scan",
      className: "bg-red-50 text-red-700",
      Icon: AlertCircle,
    };
  }

  if (status === "unknown") {
    return {
      label: "Status unavailable",
      className: "bg-amber-50 text-amber-700",
      Icon: AlertCircle,
    };
  }

  return {
    label: "Scanning",
    className: "bg-blue-50 text-blue-700",
    Icon: Clock,
  };
};

const ProtectedDocumentUploadPanel: React.FC<
  ProtectedDocumentUploadPanelProps
> = ({
  userId,
  vcardCategory,
  attachmentKey,
  files,
  onFilesChange,
  disabled = false,
  title,
  description,
}) => {
  const config = getAttachmentConfig(attachmentKey);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [openingFileId, setOpeningFileId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingStoredFiles, setIsLoadingStoredFiles] = useState(false);
  const [scanStatuses, setScanStatuses] = useState<FileStatusMap>({});
  const loadStoredFilesRequestIdRef = useRef(0);
  const filesRef = useRef<SmartTagAttachmentFile[]>(files);
  // Cache of object URLs keyed by file storagePath. Created on first Open,
  // reused for subsequent opens of the same file in the same session, and
  // revoked en masse on unmount to release Blob memory (PRD §13/§14).
  const openedObjectUrlsRef = useRef<Map<string, string>>(new Map());

  const acceptedTypes = useMemo(
    () => config.allowedMimeTypes.join(","),
    [config.allowedMimeTypes],
  );

  const resolvedTitle = title || config.label;
  const resolvedDescription = description || config.description;
  const attachmentDirectoryPath = useMemo(
    () => `smarttag-attachments/${userId}/${vcardCategory}/${attachmentKey}`,
    [attachmentKey, userId, vcardCategory],
  );

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    const reconcileWithStorage = async () => {
      const requestId = ++loadStoredFilesRequestIdRef.current;

      const setLoadingIfCurrent = (next: boolean) => {
        if (loadStoredFilesRequestIdRef.current === requestId) {
          setIsLoadingStoredFiles(next);
        }
      };

      setLoadingIfCurrent(true);

      try {
        const storedItems = await listAll(
          ref(storage, attachmentDirectoryPath),
        );
        const loadedFiles = await Promise.all(
          storedItems.items.map(async (itemRef) => {
            const metadata = await getMetadata(itemRef);
            const originalName = metadata.customMetadata?.originalFileName;

            return {
              id: itemRef.fullPath,
              name:
                originalName && originalName.trim().length > 0
                  ? originalName
                  : getStorageDisplayName(itemRef.name),
              storagePath: itemRef.fullPath,
              contentType: metadata.contentType || "application/octet-stream",
              sizeBytes: metadata.size,
              uploadedAt: metadata.timeCreated || new Date().toISOString(),
              visibility: config.defaultVisibility,
            } satisfies SmartTagAttachmentFile;
          }),
        );

        if (loadStoredFilesRequestIdRef.current !== requestId) {
          return;
        }

        // Storage is the source of truth. Compare to current state by
        // storagePath; only call onFilesChange when something actually
        // differs (avoids gratuitous Firestore writes on every mount).
        const currentPaths = filesRef.current
          .map((file) => file.storagePath)
          .sort();
        const storedPaths = loadedFiles.map((file) => file.storagePath).sort();
        const isDifferent =
          currentPaths.length !== storedPaths.length ||
          currentPaths.some((path, index) => path !== storedPaths[index]);

        if (isDifferent) {
          await onFilesChange(loadedFiles);
        }
      } catch (error) {
        if (loadStoredFilesRequestIdRef.current === requestId) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Could not load saved documents. Please refresh and try again.",
          );
        }
      } finally {
        setLoadingIfCurrent(false);
      }
    };

    if (!userId) {
      return;
    }

    void reconcileWithStorage();

    const handleVisibilityOrFocus = () => {
      if (
        document.visibilityState === "visible" &&
        Boolean(userId) &&
        !isUploading
      ) {
        void reconcileWithStorage();
      }
    };

    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [
    attachmentDirectoryPath,
    config.defaultVisibility,
    isUploading,
    onFilesChange,
    userId,
  ]);

  useEffect(() => {
    let isMounted = true;

    const resolveStatuses = async () => {
      const entries = await Promise.all(
        files.map(async (file) => {
          const status = await resolveAttachmentScanStatus(
            ref(storage, file.storagePath),
          ).catch((): AttachmentScanStatus => "unknown");

          return [file.id, status] as const;
        }),
      );

      if (isMounted) {
        const nextStatuses = Object.fromEntries(entries);
        setScanStatuses(nextStatuses);

        const missingPaths = new Set(
          files
            .filter((file) => nextStatuses[file.id] === "missing")
            .map((file) => file.storagePath),
        );

        if (missingPaths.size > 0) {
          const nextFiles = files.filter(
            (file) => !missingPaths.has(file.storagePath),
          );
          await onFilesChange(nextFiles);
        }
      }
    };

    if (files.length === 0) {
      setScanStatuses({});
      return;
    }

    void resolveStatuses();

    return () => {
      isMounted = false;
    };
  }, [files, onFilesChange]);

  // Revoke any cached object URLs when the panel unmounts. Blob memory is
  // released and any leaked Open links die with the session.
  useEffect(() => {
    const cache = openedObjectUrlsRef.current;
    return () => {
      for (const url of cache.values()) {
        URL.revokeObjectURL(url);
      }
      cache.clear();
    };
  }, []);

  const openFile = async (file: SmartTagAttachmentFile) => {
    if (disabled || openingFileId) {
      return;
    }

    if (scanStatuses[file.id] !== "clean") {
      return;
    }

    setErrorMessage(null);
    setOpeningFileId(file.id);

    try {
      const cache = openedObjectUrlsRef.current;
      let objectUrl = cache.get(file.storagePath);

      if (!objectUrl) {
        const blob = await fetchAttachmentBlob(ref(storage, file.storagePath));
        objectUrl = URL.createObjectURL(blob);
        cache.set(file.storagePath, objectUrl);
      }

      // Open in a new tab. Browser-native PDF viewer / image viewer renders
      // it; .docx auto-downloads. The user can save from the new tab using
      // the browser's built-in save mechanisms.
      window.open(objectUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Could not open this document. Please try again.",
      );
    } finally {
      setOpeningFileId(null);
    }
  };

  const addFiles = async (selectedFiles: FileList | File[]) => {
    const nextSelectedFiles = Array.from(selectedFiles);
    if (nextSelectedFiles.length === 0 || disabled || isUploading) {
      return;
    }

    setErrorMessage(null);

    // Per-card file count ceiling. Configured per attachment key in the
    // registry. Caught client-side here before any upload to Storage.
    if (files.length + nextSelectedFiles.length > config.maxFilesPerCard) {
      setErrorMessage(
        `You can attach up to ${config.maxFilesPerCard} ${
          config.maxFilesPerCard === 1 ? "document" : "documents"
        }. Remove ${
          files.length === 0 ? "some files" : "an existing document"
        } to add more.`,
      );
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    setIsUploading(true);

    try {
      const uploadedFiles: SmartTagAttachmentFile[] = [];

      for (const file of nextSelectedFiles) {
        validateAttachmentFile(file, attachmentKey);

        const uploadFileName = buildProtectedUploadFileName(
          file.name,
          attachmentKey,
        );
        const storagePath = `${attachmentDirectoryPath}/${uploadFileName}`;

        await uploadProtectedFile({
          storage,
          storagePath,
          file,
        });

        uploadedFiles.push({
          id: createAttachmentId(),
          name: file.name,
          storagePath,
          contentType: file.type,
          sizeBytes: file.size,
          uploadedAt: new Date().toISOString(),
          visibility: config.defaultVisibility,
        });
      }

      if (uploadedFiles.length > 0) {
        await onFilesChange([...files, ...uploadedFiles]);
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const removeFile = async (file: SmartTagAttachmentFile) => {
    if (disabled || removingFileId) {
      return;
    }

    const shouldRemove = window.confirm(
      `Remove "${file.name}" from this SmartTag? This will delete the uploaded document.`,
    );

    if (!shouldRemove) {
      return;
    }

    setErrorMessage(null);
    setRemovingFileId(file.id);

    try {
      await deleteObject(ref(storage, file.storagePath)).catch((error) => {
        const code =
          typeof error === "object" && error !== null && "code" in error
            ? (error as { code?: unknown }).code
            : null;

        if (code !== "storage/object-not-found") {
          throw error;
        }
      });

      // If we ever opened this file in this session, drop its cached object
      // URL too. Future re-opens (e.g. of a same-named upload) will re-fetch.
      const cachedUrl = openedObjectUrlsRef.current.get(file.storagePath);
      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
        openedObjectUrlsRef.current.delete(file.storagePath);
      }

      await onFilesChange(
        files.filter((item) => item.storagePath !== file.storagePath),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Could not remove this document. Please try again.",
      );
    } finally {
      setRemovingFileId(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void addFiles(event.dataTransfer.files);
  };

  return (
    <section className="w-full min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/40">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {resolvedTitle}
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {resolvedDescription}
          </p>
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-5 text-center transition-colors ${
          isDragging
            ? "border-red-400 bg-red-50"
            : "border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50/50"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        {isUploading ? (
          <Loader2 className="mb-3 h-8 w-8 animate-spin text-red-600" />
        ) : (
          <Upload className="mb-3 h-8 w-8 text-gray-500" />
        )}
        <p className="text-sm font-medium text-gray-900">
          {isUploading ? "Uploading document..." : "Drop files here or browse"}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          PDF, DOC, DOCX, JPG, PNG, WEBP up to{" "}
          {formatBytes(config.maxSizeBytes)}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptedTypes}
        className="hidden"
        aria-label={`Upload ${config.label}`}
        disabled={disabled || isUploading}
        onChange={(event) => {
          if (event.target.files) {
            void addFiles(event.target.files);
          }
        }}
      />

      {errorMessage && (
        <div
          role="alert"
          className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {isLoadingStoredFiles && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading saved documents...</span>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((file, index) => {
            const statusTone = getStatusTone(scanStatuses[file.id]);
            const StatusIcon = statusTone.Icon;
            const isRemoving = removingFileId === file.id;

            return (
              <div
                key={file.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <FileText className="h-5 w-5 flex-shrink-0 text-gray-500" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {index + 1}. {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatBytes(file.sizeBytes)}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${statusTone.className}`}
                  >
                    {scanStatuses[file.id] === "clean" ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <StatusIcon className="h-3.5 w-3.5" />
                    )}
                    {statusTone.label}
                  </span>
                  {(() => {
                    const isOpening = openingFileId === file.id;
                    const isOpenable = scanStatuses[file.id] === "clean";
                    const openTitle = isOpenable
                      ? `Open ${file.name} in a new tab`
                      : "Available once the scan is clean";
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          void openFile(file);
                        }}
                        disabled={disabled || isOpening || !isOpenable}
                        className="rounded-md p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Open ${file.name}`}
                        title={openTitle}
                      >
                        {isOpening ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => {
                      void removeFile(file);
                    }}
                    disabled={disabled || isRemoving}
                    className="rounded-md p-2 text-gray-500 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`Remove ${file.name}`}
                    title={`Remove ${file.name}`}
                  >
                    {isRemoving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default ProtectedDocumentUploadPanel;
